// package
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
    Download,
    Frown,
    Image as ImageIcon,
    LayoutGrid,
    Link2,
    RotateCw,
    Share2,
    Smile,
    X,
} from 'lucide-react';
import QrScanner from 'qr-scanner';

// custom
import QRCode from 'qrcode';
import { getUserInfo } from '@shared/api/client';
import { usePageLayout } from '@/app/router';
import { ROUTES } from '@/app/router/routes';
import { get54QrcodeUserCode, get95QrcodeUserCheck, post55QrcodeUserCheckin } from '../api/qrcodeService';
import { getViewerToken } from '@features/auth/api/authService';
import { useLanguage } from '@shared/contexts/LanguageContext';

/** 掃描畫面版型參考：src/features/qr-code/IMG_6368.png */
/** 個人 QR 浮層版型參考：src/features/qr-code/IMG_6369.png */
/** URL query：`?mode=qr` 進入頁面時直接顯示個人行動條碼（其餘或未帶參數為掃描模式） */

const formatRemainingUntilExpiry = (expiryMs, nowMs) => {
    if (expiryMs == null || !Number.isFinite(expiryMs)) return '0:00';
    const msLeft = Math.max(0, expiryMs - nowMs);
    const totalSec = Math.floor(msLeft / 1000);
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    const s = totalSec % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
    return `${String(m).padStart(1)}:${String(s).padStart(2, '0')}`;
};

const isMobileChromeBrowser = () => {
    if (typeof navigator === 'undefined') return false;
    const ua = navigator.userAgent || '';
    const isMobile = /Android|iPhone|iPad|iPod|Mobile/i.test(ua);
    // 排除 iOS 上的 CriOS / FxiOS / EdgiOS 等非 Chromium 核心情境。
    const isChromeLike = /Chrome\/\d+/i.test(ua) && !/Edg|OPR|SamsungBrowser|CriOS/i.test(ua);
    return isMobile && isChromeLike;
};

//---------------------------------------------------------------------------
const QrcodePage = ({
    heroData = {
        image: '',
        title: 'QR Code活動簽到',
        description: '',
    },
}) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { setLayoutOptions } = usePageLayout();
    const { t } = useLanguage();

    const pageMode = searchParams.get('mode');
    const openQrOnEntry = pageMode === 'qr';

    useEffect(() => {
        setLayoutOptions({ hideHeader: true, hideBottomNav: true, hideFooter: true });
        return () => setLayoutOptions({});
    }, [setLayoutOptions]);

    const userInfo = useMemo(() => getUserInfo(), []);
    const userName = userInfo?.name || t('qr-code.notLoggedIn');
    const isLogin = Boolean(userInfo?.name);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [code, setCode] = useState('');
    const [userCodeExpiryMs, setUserCodeExpiryMs] = useState(null);
    const [qrDataUrl, setQrDataUrl] = useState(null);

    const [scanMode, setScanMode] = useState(true);
    const [bottomSheetMode, setBottomSheetMode] = useState(openQrOnEntry ? 'qr' : 'none');
    const bottomSheetModeRef = useRef(bottomSheetMode);
    bottomSheetModeRef.current = bottomSheetMode;
    const [bottomTab, setBottomTab] = useState('scan');
    const [manualCodeInput, setManualCodeInput] = useState('');

    const [checkinInProgress, setCheckinInProgress] = useState(false);
    const [checkinSubmitLocked, setCheckinSubmitLocked] = useState(false);
    const [scanResult, setScanResult] = useState(null);
    const [scannedOfficialCode, setScannedOfficialCode] = useState('');
    const [showAlignHint, setShowAlignHint] = useState(false);
    const [formatFeedback, setFormatFeedback] = useState(null);
    const [cameraError, setCameraError] = useState(null);
    /** 個人 QR 模式：get95 輪詢，收到訊息後顯示至下一次輪詢 */
    const [qrUserCheckMsg, setQrUserCheckMsg] = useState(null);
    /** 個人 QR 浮層：強制每秒重繪以更新 expiry 倒數 */
    const [qrExpiryCountdownTick, setQrExpiryCountdownTick] = useState(0);

    const videoRef = useRef(null);
    const scanActiveRef = useRef(false);
    const checkinSubmittedRef = useRef(false);
    const qrScannerRef = useRef(null);
    const streamRef = useRef(null);

    const formatFeedbackTimeoutRef = useRef(null);
    const alignHintTimeoutRef = useRef(null);
    const formatFeedbackKindRef = useRef(null);
    
    /** E：活動官方碼簽到；G：兌換碼；U：個人碼（與他人互轉積分）。皆為 1 字母 + 10 位數或 15 位數
     * e.g. 活動QR碼E9241562527；兌換碼G0011923171 */
    const OFFICIAL_CODE_RE = /^(?:E|G|U)(?:\d{10}|\d{15})$/;

    /** 與 qr-scanner 內部 _setVideoMirror 一致：僅前鏡頭水平翻轉，避免串流已存在時 library 略過設 transform */
    const syncVideoMirrorFromTrack = () => {
        const el = videoRef.current;
        if (!el) return;
        const stream = el.srcObject;
        if (!(stream instanceof MediaStream)) return;
        const track = stream.getVideoTracks()[0];
        if (!track || track.readyState !== 'live') return;
        const settings = typeof track.getSettings === 'function' ? track.getSettings() : {};
        let { facingMode } = settings;
        if (!facingMode && track.label) {
            if (/rear|back|environment/i.test(track.label)) facingMode = 'environment';
            else if (/front|user|face/i.test(track.label)) facingMode = 'user';
        }
        el.style.transform = facingMode === 'user' ? 'scaleX(-1)' : 'scaleX(1)';
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);
        setError(null);
        setCode('');
        setQrDataUrl(null);

        get54QrcodeUserCode()
            .then((res) => {
                if (!isMounted) return;
                const data = res?.data ?? res ?? {};
                const codeValue = data?.code;
                setCode(String(codeValue ?? ''));
                const expiryMs = Date.parse(String(data?.expiry ?? ''));
                setUserCodeExpiryMs(Number.isFinite(expiryMs) ? expiryMs : null);
            })
            .catch((err) => {
                if (!isMounted) return;
                console.error('Failed to load qrcode user code:', err);
                setError(err);
            })
            .finally(() => {
                if (!isMounted) return;
                setLoading(false);
            });

        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        if (!code) return;

        const generate = async () => {
            try {
                const url = await QRCode.toDataURL(code, {
                    width: 280,
                    margin: 2,
                    errorCorrectionLevel: 'M',
                });
                if (cancelled) return;
                setQrDataUrl(url || null);
            } catch (e) {
                if (cancelled) return;
                console.error('Failed to generate QR:', e);
                setQrDataUrl(null);
            }
        };

        generate();
        return () => {
            cancelled = true;
        };
    }, [code]);

    const onClickBack = () => {
        navigate(-1);
    };
    const onClickGoHome = () => {
        navigate(ROUTES.HOME);
    };

    const clearTimeoutSafe = (tRef) => {
        if (tRef?.current) clearTimeout(tRef.current);
        tRef.current = null;
    };

    const showAlignHintFor3Seconds = () => {
        clearTimeoutSafe(alignHintTimeoutRef);
        setShowAlignHint(true);
        alignHintTimeoutRef.current = setTimeout(() => {
            setShowAlignHint(false);
        }, 3000);
    };

    const showFormatFeedback = (kind, customText) => {
        const text = customText ?? (kind === 'ok' ? t('qr-code.formatOk') : t('qr-code.formatError'));
        formatFeedbackKindRef.current = kind;
        setFormatFeedback({ kind, text });
        setShowAlignHint(false);
        clearTimeoutSafe(alignHintTimeoutRef);
        clearTimeoutSafe(formatFeedbackTimeoutRef);
        formatFeedbackTimeoutRef.current = setTimeout(() => {
            formatFeedbackKindRef.current = null;
            setFormatFeedback(null);
        }, 3000);
    };

    const stopScanner = () => {
        scanActiveRef.current = false;
        const scanner = qrScannerRef.current;
        if (scanner) {
            try {
                scanner.stop();
            } catch (e) {
                // ignore
            }
            try {
                scanner.destroy();
            } catch (e) {
                // ignore
            }
            qrScannerRef.current = null;
        }
    };

    const stopCameraStream = () => {
        stopScanner();
        const stream = streamRef.current || videoRef.current?.srcObject;
        if (stream?.getTracks) {
            stream.getTracks().forEach((track) => track.stop());
        }
        streamRef.current = null;
        if (videoRef.current) {
            videoRef.current.pause();
            // eslint-disable-next-line no-param-reassign
            videoRef.current.srcObject = null;
        }
    };

    const acquireCheckinSubmitLock = () => {
        if (checkinSubmittedRef.current) return false;
        checkinSubmittedRef.current = true;
        setCheckinSubmitLocked(true);
        return true;
    };

    const postQrcodeUserCheckinFlow = async (official_code) => {
        setScannedOfficialCode(official_code);
        setScanResult(null);

        stopCameraStream();
        showFormatFeedback('ok');

        await getViewerToken();
        const apiPromise = post55QrcodeUserCheckin(official_code);

        await new Promise((r) => setTimeout(r, 3000));

        setScanMode(false);
        setBottomSheetMode('result');
        if (isLogin) setCheckinInProgress(true);

        try {
            const res = await apiPromise;
            const data = res?.data ?? res ?? {};
            const resultCode = data?.result_code;
            const isSuccess = String(resultCode) === '200';
            const message =
                data?.scan_msg ||
                data?.message ||
                (isSuccess ? '簽到成功' : '簽到失敗');

            setScanResult({
                status: isSuccess ? 'success' : 'failure',
                message,
            });
        } catch (e) {
            const data = e?.data ?? e ?? {};
            const message =
                data?.scan_msg ||
                data?.message ||
                '簽到錯誤，請重新掃描';
            setScanResult({ status: 'error', message });
        } finally {
            if (isLogin) setCheckinInProgress(false);
        }
    };

    const processScanCandidate = useCallback(
        (raw) => {
            if (checkinSubmittedRef.current) return;
            if (bottomSheetModeRef.current !== 'none') return;
            const candidate = String(raw ?? '').trim();
            if (!candidate) return;

            if (!OFFICIAL_CODE_RE.test(candidate)) {
                setScannedOfficialCode(candidate);
                if (formatFeedbackKindRef.current !== 'error') {
                    showFormatFeedback('error');
                }
                return;
            }

            if (!acquireCheckinSubmitLock()) return;
            setScannedOfficialCode(candidate);
            void postQrcodeUserCheckinFlow(candidate);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isLogin],
    );

    const startScanner = async () => {
        /** 須先釋放既有串流：若 video 已在 play，之後 QrScanner 只會 await play()，不會再觸發 play 事件，_scanFrame 永遠不會啟動 */
        stopCameraStream();
        setCameraError(null);

        try {
            if (!videoRef.current) {
                setCameraError(t('qr-code.cameraNotReady'));
                setScanMode(false);
                return;
            }

            const hasCamera = await QrScanner.hasCamera();
            if (!hasCamera) {
                setCameraError(t('qr-code.noCamera'));
                setScanMode(false);
                return;
            }

            scanActiveRef.current = true;

            qrScannerRef.current = new QrScanner(
                videoRef.current,
                (result) => {
                    if (checkinSubmittedRef.current) return;
                    if (bottomSheetModeRef.current !== 'none') return;
                    processScanCandidate(result?.data);
                },
                {
                    preferredCamera: 'environment',
                    maxScansPerSecond: 10,
                    returnDetailedScanResult: true,
                    onDecodeError: () => {},
                },
            );

            await qrScannerRef.current.start();
            if (videoRef.current?.srcObject instanceof MediaStream) {
                streamRef.current = videoRef.current.srcObject;
            }
            syncVideoMirrorFromTrack();
        } catch (e) {
            console.error('Failed to start scanner:', e);
            if (isMobileChromeBrowser() && (e?.name === 'NotAllowedError' || e?.name === 'PermissionDeniedError')) {
                setCameraError(t('qr-code.chromeCameraPermission'));
            } else {
                setCameraError(t('qr-code.cameraOpenFailed'));
            }
            setScanMode(false);
        }
    };

    const enterScanMode = () => {
        stopCameraStream();
        checkinSubmittedRef.current = false;
        setCheckinSubmitLocked(false);
        formatFeedbackKindRef.current = null;
        setFormatFeedback(null);
        clearTimeoutSafe(formatFeedbackTimeoutRef);
        clearTimeoutSafe(alignHintTimeoutRef);
        setShowAlignHint(false);
        setCameraError(null);
        setScanResult(null);
        setScannedOfficialCode('');
        setCheckinInProgress(false);
        setBottomSheetMode('none');
        setBottomTab('scan');
        setManualCodeInput('');
        showAlignHintFor3Seconds();
        setScanMode(true);
    };

    const refreshUserCode = async () => {
        setLoading(true);
        setError(null);
        try {
            const res = await get54QrcodeUserCode();
            const data = res?.data ?? res ?? {};
            setCode(String(data?.code ?? ''));
            const expiryMs = Date.parse(String(data?.expiry ?? ''));
            setUserCodeExpiryMs(Number.isFinite(expiryMs) ? expiryMs : null);
        } catch (err) {
            console.error('refreshUserCode:', err);
            setError(err);
        } finally {
            setLoading(false);
        }
    };

    const enterQrMode = async () => {
        await refreshUserCode();
        setBottomSheetMode('qr');
        setBottomTab('scan');
        setShowAlignHint(false);
        setFormatFeedback(null);
        formatFeedbackKindRef.current = null;
        clearTimeoutSafe(formatFeedbackTimeoutRef);
        clearTimeoutSafe(alignHintTimeoutRef);
        setCameraError(null);
        setScanResult(null);
        setScannedOfficialCode('');
        setCheckinInProgress(false);
    };

    const closePersonalQrModal = () => {
        setBottomSheetMode('none');
        setScanMode(true);
        showAlignHintFor3Seconds();
    };

    const onClickRestart = () => {
        enterScanMode();
    };

    const onCopyPersonalCode = async () => {
        if (!code) return;
        try {
            await navigator.clipboard.writeText(code);
            showFormatFeedback('ok', t('qr-code.copiedCode'));
        } catch (e) {
            showFormatFeedback('error', t('qr-code.copyFailed'));
        }
    };

    const onSharePersonal = async () => {
        if (!code) return;
        const payload = { title: heroData.title || '活動簽到', text: code };
        try {
            if (navigator.share) {
                await navigator.share(payload);
            } else {
                await onCopyPersonalCode();
            }
        } catch (e) {
            if (e?.name !== 'AbortError') {
                await onCopyPersonalCode();
            }
        }
    };

    const onSaveQrImage = () => {
        if (!qrDataUrl) return;
        const a = document.createElement('a');
        a.href = qrDataUrl;
        a.download = `qrcode-${code || 'checkin'}.png`;
        a.click();
    };

    const onGalleryChange = async (e) => {
        const file = e.target.files?.[0];
        // eslint-disable-next-line no-param-reassign
        e.target.value = '';
        if (!file) return;
        try {
            const result = await QrScanner.scanImage(file);
            const data = typeof result === 'string' ? result : result?.data;
            processScanCandidate(data);
        } catch (err) {
            console.error('scanImage:', err);
            showFormatFeedback('error', t('qr-code.photoQrFailed'));
        }
    };

    const onSubmitManualCode = () => {
        processScanCandidate(manualCodeInput);
    };

    useEffect(() => {
        showAlignHintFor3Seconds();
        return () => {
            clearTimeoutSafe(alignHintTimeoutRef);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        const shouldRunScanner = scanMode && bottomTab === 'scan' && bottomSheetMode === 'none';
        if (!shouldRunScanner) {
            stopCameraStream();
            return undefined;
        }
        void startScanner();
        return () => stopCameraStream();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scanMode, bottomTab, bottomSheetMode]);

    const toQrUserCheckMessage = useCallback((payload, statusMessage = '') => {
        if (payload == null) return null;

        if (typeof payload === 'object') {
            const messageRaw = payload.scan_msg;
            const message = messageRaw == null ? '' : String(messageRaw).trim();
            if (!message) return null;
            return {
                message,
                isSuccess: String(statusMessage).trim() === 'Success',
            };
        }

        return null;
    }, []);

    // 顯示行動條碼模式：expiry 未到期前每 10 秒輪詢 get95；僅 activity、transfer 皆不為 null 時顯示 scan_msg
    useEffect(() => {
        if (bottomSheetMode !== 'qr') {
            setQrUserCheckMsg(null);
            return undefined;
        }

        if (userCodeExpiryMs != null && Date.now() >= userCodeExpiryMs) {
            setQrUserCheckMsg(null);
            return undefined;
        }

        let cancelled = false;
        let intervalId = null;
        let expiryTimeoutId = null;
        const poll = async () => {
            // 上一輪訊息顯示到下一次輪詢開始時即清除
            // setQrUserCheckMsg(null);
            if (userCodeExpiryMs != null && Date.now() >= userCodeExpiryMs) {
                if (intervalId) clearInterval(intervalId);
                return;
            }
            try {
                const res = await get95QrcodeUserCheck();
                if (cancelled) return;
                const data = res?.data ?? res ?? {};
                const rootMessage = toQrUserCheckMessage(data, data.message);
                if (rootMessage) {
                    setQrUserCheckMsg(rootMessage);
                } else {
                    const a = toQrUserCheckMessage(data.activity, data.message);
                    const t = toQrUserCheckMessage(data.transfer, data.message);
                    // activity 與 transfer 都為 null（或無 scan_msg）時，不顯示任何結果訊息
                    setQrUserCheckMsg(a || t || null);
                }
            } catch (e) {
                if (!cancelled) {
                    console.error('get95QrcodeUserCheck:', e);
                    setQrUserCheckMsg(null);
                }
            }
        };
        void poll();
        intervalId = setInterval(poll, 10000);

        if (userCodeExpiryMs != null) {
            const msToExpiry = userCodeExpiryMs - Date.now();
            if (msToExpiry > 0) {
                expiryTimeoutId = setTimeout(() => {
                    cancelled = true;
                    if (intervalId) clearInterval(intervalId);
                    setQrUserCheckMsg(null);
                }, msToExpiry);
            }
        }

        return () => {
            cancelled = true;
            if (intervalId) clearInterval(intervalId);
            if (expiryTimeoutId) clearTimeout(expiryTimeoutId);
            setQrUserCheckMsg(null);
        };
    }, [bottomSheetMode, toQrUserCheckMessage, userCodeExpiryMs]);

    useEffect(() => {
        if (bottomSheetMode !== 'qr' || userCodeExpiryMs == null || !Number.isFinite(userCodeExpiryMs)) {
            return undefined;
        }
        if (Date.now() >= userCodeExpiryMs) {
            setQrExpiryCountdownTick((n) => n + 1);
            return undefined;
        }
        const id = setInterval(() => {
            setQrExpiryCountdownTick((n) => n + 1);
            if (Date.now() >= userCodeExpiryMs) {
                clearInterval(id);
            }
        }, 1000);
        const msToExpiry = userCodeExpiryMs - Date.now();
        const expiryOnceId =
            msToExpiry > 0
                ? setTimeout(() => {
                      setQrExpiryCountdownTick((n) => n + 1);
                  }, msToExpiry + 1)
                : null;
        return () => {
            clearInterval(id);
            if (expiryOnceId) clearTimeout(expiryOnceId);
        };
    }, [bottomSheetMode, userCodeExpiryMs]);

    const FRAME = 240;
    const lineGrey = '#8E8E8E';
    const borderLight = '#E6E6E6';
    const borderDark = '#111';

    const personalQrExpiryCaption = useMemo(() => {
        if (bottomSheetMode !== 'qr') return null;
        if (userCodeExpiryMs == null || !Number.isFinite(userCodeExpiryMs)) return null;
        const now = Date.now();
        const countdownStr = formatRemainingUntilExpiry(userCodeExpiryMs, now);
        const expired = userCodeExpiryMs <= now;
        return { countdownStr, expired };
    }, [bottomSheetMode, userCodeExpiryMs, qrExpiryCountdownTick]);
    const isUserCodeStillValid = Number.isFinite(userCodeExpiryMs) && Date.now() < userCodeExpiryMs;
    const isUpdateCodeDisabled = loading || isUserCodeStillValid;

    const statusPrimary = useMemo(() => {
        if (bottomSheetMode === 'result') {
            if (checkinInProgress || !scanResult) return t('qr-code.checkingIn');
            return `${scanResult.message}`;
        }
        if (cameraError) return `${cameraError}`;
        if (checkinInProgress) return t('qr-code.statusCheckingIn');
        if (formatFeedback) return `${formatFeedback.text}`;
        if (scannedOfficialCode) return `${scannedOfficialCode}`;
        return t('qr-code.scanHint');
    }, [
        bottomSheetMode,
        cameraError,
        checkinInProgress,
        formatFeedback,
        scanResult,
        scannedOfficialCode,
    ]);

    const recoveryHint = useMemo(() => {
        if (cameraError) return t('qr-code.permissionHint');
        if (formatFeedback?.kind === 'error') return t('qr-code.barcodeError');
        if (bottomTab === 'text') return t('qr-code.manualInputHint');
        return t('qr-code.autoSubmitHint');
    }, [bottomTab, cameraError, formatFeedback?.kind, t]);

    const showScanChrome =
        bottomSheetMode === 'none' &&
        !formatFeedback &&
        !checkinInProgress;
    const showCameraPermissionTag = bottomSheetMode === 'none' && Boolean(cameraError) && isMobileChromeBrowser();

    return (
        <section
            style={{
                width: '100%',
                maxWidth: 430,
                minHeight: '100vh',
                margin: '0 auto',
                position: 'relative',
                background: '#FFFFFF',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                boxSizing: 'border-box',
            }}
        >
            {/* 相機區（參考 IMG_6368：上半部取景 + 暗角 + 掃描框） */}
            <div
                style={{
                    flex: '1 1 58%',
                    minHeight: 360,
                    position: 'relative',
                    width: '100%',
                    background: '#000',
                }}
            >
                <video
                    ref={videoRef}
                    playsInline
                    muted
                    controlsList="nodownload"
                    onContextMenu={(e) => e.preventDefault()}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                    }}
                    aria-label={t('qr-code.cameraAria')}
                />

                {/* 暗角 + 中央透明掃描區 */}
                <div
                    style={{
                        position: 'absolute',
                        inset: 0,
                        pointerEvents: 'none',
                    }}
                >
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '44%',
                            width: FRAME,
                            height: FRAME,
                            transform: 'translate(-50%, -50%)',
                            borderRadius: 10,
                            boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)',
                            background: 'transparent',
                        }}
                    />
                    {/* L 型四角（對齊掃描框外緣） */}
                    {(() => {
                        const corner = 28;
                        const bw = '3px solid rgba(255,255,255,0.95)';
                        const base = {
                            position: 'absolute',
                            width: corner,
                            height: corner,
                            pointerEvents: 'none',
                        };
                        const half = FRAME / 2;
                        return (
                            <>
                                <div
                                    style={{
                                        ...base,
                                        left: `calc(50% - ${half}px)`,
                                        top: `calc(44% - ${half}px)`,
                                        borderTop: bw,
                                        borderLeft: bw,
                                    }}
                                />
                                <div
                                    style={{
                                        ...base,
                                        left: `calc(50% + ${half}px - ${corner}px)`,
                                        top: `calc(44% - ${half}px)`,
                                        borderTop: bw,
                                        borderRight: bw,
                                    }}
                                />
                                <div
                                    style={{
                                        ...base,
                                        left: `calc(50% - ${half}px)`,
                                        top: `calc(44% + ${half}px - ${corner}px)`,
                                        borderBottom: bw,
                                        borderLeft: bw,
                                    }}
                                />
                                <div
                                    style={{
                                        ...base,
                                        left: `calc(50% + ${half}px - ${corner}px)`,
                                        top: `calc(44% + ${half}px - ${corner}px)`,
                                        borderBottom: bw,
                                        borderRight: bw,
                                    }}
                                />
                            </>
                        );
                    })()}
                </div>

                {/* 右上角關閉（參考圖） */}
                <button
                    type="button"
                    onClick={onClickBack}
                    aria-label={t('qr-code.closeAria')}
                    style={{
                        position: 'absolute',
                        top: 14,
                        right: 14,
                        width: 44,
                        height: 44,
                        border: 'none',
                        background: 'transparent',
                        padding: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        zIndex: 2,
                    }}
                >
                    <X size={36} color="#fff" strokeWidth={2.2} />
                </button>

                {/* 中央提示（鏡頭錯誤 / 對準 / 格式） */}
                {(cameraError || formatFeedback || showAlignHint) && bottomSheetMode === 'none' ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: '50%',
                            top: '58%',
                            transform: 'translateX(-50%)',
                            maxWidth: '86%',
                            padding: '10px 14px',
                            borderRadius: 12,
                            background: 'rgba(255, 255, 255, 0.92)',
                            color: cameraError
                                ? '#E20020'
                                : formatFeedback
                                ? formatFeedback.kind === 'ok'
                                    ? '#00A86B'
                                    : '#E20020'
                                : '#222',
                            fontSize: 15,
                            fontWeight: 800,
                            textAlign: 'center',
                            zIndex: 2,
                            pointerEvents: 'none',
                        }}
                    >
                        {cameraError ? cameraError : formatFeedback ? formatFeedback.text : t('qr-code.alignQrHint')}
                    </div>
                ) : null}

                {/* 底部：顯示行動條碼 + 相簿（參考 IMG_6368） */}
                {showScanChrome ? (
                    <div
                        style={{
                            position: 'absolute',
                            left: 16,
                            right: 16,
                            bottom: 18,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 12,
                            zIndex: 2,
                        }}
                    >
                        <button
                            type="button"
                            onClick={enterQrMode}
                            disabled={loading}
                            aria-label={t('qr-code.showQrAria')}
                            style={{
                                flex: 1,
                                height: 48,
                                borderRadius: 999,
                                border: 'none',
                                cursor: loading ? 'not-allowed' : 'pointer',
                                opacity: loading ? 0.65 : 1,
                                background: 'rgba(0, 0, 0, 0.45)',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 800,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                gap: 10,
                                backdropFilter: 'blur(8px)',
                            }}
                        >
                            <LayoutGrid size={20} aria-hidden />
                            {t('qr-code.scanMobileBarcode')}
                        </button>
                    </div>
                ) : null}
            </div>

            {/* 下方白底說明 + 分頁（參考 IMG_6368） */}
            <div
                style={{
                    flex: '0 0 auto',
                    background: '#fff',
                    padding: '18px 20px 10px',
                    boxSizing: 'border-box',
                    zIndex: 10,
                }}
            >
                <p
                    style={{
                        margin: '10px 0 0',
                        textAlign: 'center',
                        color: '#222',
                        fontSize: 16,
                        lineHeight: 1.45,
                        fontWeight: 700,
                    }}
                >
                    {statusPrimary}
                </p>
                <p
                    style={{
                        margin: '6px 0 0',
                        textAlign: 'center',
                        color: lineGrey,
                        fontSize: 13,
                        lineHeight: 1.45,
                    }}
                >
                    {recoveryHint}
                </p>
                {showCameraPermissionTag ? (
                    <div style={{ marginTop: 8, textAlign: 'center' }}>
                        <permission type="camera" />
                    </div>
                ) : null}
                {/* <p
                    style={{
                        margin: '8px 0 0',
                        textAlign: 'center',
                        color: lineGrey,
                        fontSize: 12,
                    }}
                >
                    帳號：{userName}
                </p> */}

                {bottomTab === 'text' ? (
                    <div style={{ marginTop: 14 }}>
                        <input
                            value={manualCodeInput}
                            onChange={(e) => setManualCodeInput(e.target.value)}
                            placeholder={t('qr-code.barcodePlaceholder')}
                            style={{
                                width: '100%',
                                height: 48,
                                borderRadius: 12,
                                border: `1px solid ${borderLight}`,
                                padding: '0 12px',
                                fontSize: 16,
                                boxSizing: 'border-box',
                            }}
                        />
                        <button
                            type="button"
                            onClick={onSubmitManualCode}
                            disabled={checkinSubmitLocked}
                            style={{
                                marginTop: 10,
                                width: '100%',
                                height: 48,
                                borderRadius: 12,
                                border: 'none',
                                background: '#111',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 800,
                                cursor: checkinSubmitLocked ? 'not-allowed' : 'pointer',
                                opacity: checkinSubmitLocked ? 0.5 : 1,
                            }}
                        >
                            {t('qr-code.confirm')}
                        </button>
                    </div>
                ) : null}

                <div
                    style={{
                        marginTop: 18,
                        display: 'flex',
                        borderTop: `1px solid ${borderLight}`,
                    }}
                >
                    <button
                        type="button"
                        onClick={() => setBottomTab('scan')}
                        style={{
                            flex: 1,
                            padding: '14px 0 10px',
                            border: 'none',
                            background: 'transparent',
                            // cursor: 'pointer',
                            fontSize: 16,
                            fontWeight: bottomTab === 'scan' ? 900 : 600,
                            color: bottomTab === 'scan' ? '#111' : lineGrey,
                            borderTop: bottomTab === 'scan' ? '3px solid #111' : '3px solid transparent',
                        }}
                    >
                        {t('qr-code.scanMobileBarcode')}
                    </button>
                </div>
            </div>

            {/* 個人 QR 全幅浮層（參考 IMG_6369） */}
            {bottomSheetMode === 'qr' ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('qr-code.mobileQrAria')}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 50,
                        background: 'rgba(0,0,0,0.68)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                    }}
                >
                    <div
                        style={{
                            background: '#fff',
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            maxHeight: '92vh',
                            overflow: 'auto',
                            position: 'relative',
                            paddingBottom: 18,
                        }}
                    >
                        <button
                            type="button"
                            onClick={closePersonalQrModal}
                            aria-label={t('qr-code.closeAria')}
                            style={{
                                position: 'absolute',
                                top: 12,
                                right: 12,
                                width: 44,
                                height: 44,
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <X size={28} color="#111" />
                        </button>

                        <div style={{ padding: '52px 20px 8px', textAlign: 'center' }}>
                            <div
                                style={{
                                    width: 280,
                                    minHeight: 280,
                                    margin: '0 auto',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxSizing: 'border-box',
                                }}
                            >
                                {qrUserCheckMsg ? (
                                    <div style={{ width: '100%' }}>
                                        {qrUserCheckMsg.isSuccess ? (
                                            <Smile size={96} color="#00A86B" style={{ margin: '8px auto 12px' }} />
                                        ) : (
                                            <Frown size={96} color="#E20020" style={{ margin: '8px auto 12px' }} />
                                        )}
                                        <div
                                            style={{
                                                fontSize: 20,
                                                fontWeight: 900,
                                                color: '#111',
                                                marginBottom: 8,
                                            }}
                                        >
                                            {qrUserCheckMsg.message}
                                        </div>
                                    </div>
                                ) : loading ? (
                                    <div style={{ color: lineGrey, fontWeight: 700 }}>{t('qr-code.qrLoading')}</div>
                                ) : error ? (
                                    <div style={{ color: '#E20020', fontWeight: 800 }}>{t('qr-code.qrLoadFailed')}</div>
                                ) : qrDataUrl ? (
                                    <img
                                        src={qrDataUrl}
                                        alt={t('qr-code.personalQrAlt')}
                                        style={{
                                            width: 280,
                                            height: 280,
                                            display: 'block',
                                        }}
                                    />
                                ) : (
                                    <div style={{ color: lineGrey, fontWeight: 700 }}>{t('qr-code.generatingQr')}</div>
                                )}
                            </div>
                        </div>

                        {!qrUserCheckMsg ? (
                            <>
                                {personalQrExpiryCaption ? (
                                    <p
                                        style={{
                                            margin: '10px 24px 0',
                                            textAlign: 'center',
                                            color: personalQrExpiryCaption.expired ? '#E20020' : '#111',
                                            fontSize: 15,
                                            lineHeight: 1.5,
                                            fontWeight: 800,
                                        }}
                                    >
                                        {`${t('qr-code.validTime')} ${personalQrExpiryCaption.countdownStr}`}
                                    </p>
                                ) : null}

                                <p
                                    style={{
                                        margin: personalQrExpiryCaption ? '6px 24px 0' : '0 24px',
                                        textAlign: 'center',
                                        color: lineGrey,
                                        fontSize: 15,
                                        lineHeight: 1.5,
                                    }}
                                >
                                    {t('qr-code.showQrDesc')}
                                </p>
                            </>
                        ) : null}

                        {/* <div
                            style={{
                                display: 'flex',
                                justifyContent: 'space-around',
                                alignItems: 'flex-start',
                                padding: '22px 8px 8px',
                            }}
                        >
                            {[
                                { icon: Download, label: '儲存', onClick: onSaveQrImage },
                            ].map(({ icon: Icon, label, onClick }) => (
                                <button
                                    key={label}
                                    type="button"
                                    onClick={onClick}
                                    style={{
                                        width: 92,
                                        border: 'none',
                                        background: 'transparent',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 8,
                                    }}
                                >
                                    <Icon size={28} color="#111" strokeWidth={1.8} />
                                    <div
                                        style={{
                                            marginTop: 8,
                                            fontSize: 13,
                                            color: '#111',
                                            fontWeight: 700,
                                        }}
                                    >
                                        {label}
                                    </div>
                                </button>
                            ))}
                        </div> */}

                        {qrUserCheckMsg ? (
                            <>
                                <div style={{ padding: '10px 20px 0' }}>
                                    <button
                                        type="button"
                                        onClick={refreshUserCode}
                                        disabled={loading}
                                        style={{
                                            width: '100%',
                                            height: 52,
                                            borderRadius: 12,
                                            border: `1px solid ${borderLight}`,
                                            background: '#111',
                                            cursor: loading ? 'not-allowed' : 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            fontSize: 16,
                                            fontWeight: 800,
                                            color: '#fff',
                                        }}
                                    >
                                        <RotateCw size={20} />
                                        {t('qr-code.showQrAgain')}
                                    </button>
                                </div>

                                <div style={{ padding: '8px 20px 0' }}>
                                    <button
                                        type="button"
                                        onClick={onClickGoHome}
                                        style={{
                                            width: '100%',
                                            height: 52,
                                            borderRadius: 12,
                                            border: `1px solid ${borderLight}`,
                                            background: '#fff',
                                            cursor: 'pointer',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 8,
                                            fontSize: 16,
                                            fontWeight: 800,
                                            color: '#111',
                                        }}
                                    >
                                        {t('qr-code.done')}
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div style={{ padding: '8px 20px 0' }}>
                                <button
                                    type="button"
                                    onClick={refreshUserCode}
                                    disabled={isUpdateCodeDisabled}
                                    style={{
                                        width: '100%',
                                        height: 52,
                                        borderRadius: 12,
                                        border: isUpdateCodeDisabled ? `2px solid ${borderLight}` : `1px solid ${borderDark}`,
                                        background: '#fff',
                                        cursor: isUpdateCodeDisabled ? 'not-allowed' : 'pointer',
                                        opacity: isUpdateCodeDisabled ? 0.4 : 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 8,
                                        fontSize: 16,
                                        fontWeight: 800,
                                        color: '#111',
                                    }}
                                >
                                    <RotateCw size={20} />
                                    {t('qr-code.refreshQr')}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            ) : null}

            {/* 簽到結果浮層（同系列白卡，不含舊版漸層按鈕） */}
            {bottomSheetMode === 'result' ? (
                <div
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('qr-code.checkinResultAria')}
                    style={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 50,
                        background: 'rgba(0,0,0,0.68)',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                    }}
                >
                    <div
                        style={{
                            position: 'relative',
                            background: '#fff',
                            borderTopLeftRadius: 20,
                            borderTopRightRadius: 20,
                            padding: '24px 20px 22px',
                            textAlign: 'center',
                        }}
                    >
                        <button
                            type="button"
                            onClick={onClickRestart}
                            aria-label={t('qr-code.closeAria')}
                            style={{
                                position: 'absolute',
                                top: 8,
                                right: 8,
                                width: 44,
                                height: 44,
                                border: 'none',
                                background: 'transparent',
                                cursor: 'pointer',
                            }}
                        >
                            <X size={28} color="#111" />
                        </button>

                        {!scanResult ? (
                            <div style={{ color: lineGrey, fontWeight: 800, padding: '40px 0' }}>{t('qr-code.checkingIn')}</div>
                        ) : (
                            <>
                                {scanResult.status === 'success' ? (
                                    <Smile size={96} color="#00A86B" style={{ margin: '8px auto 12px' }} />
                                ) : (
                                    <Frown size={96} color="#E20020" style={{ margin: '8px auto 12px' }} />
                                )}
                                <div
                                    style={{
                                        fontSize: 20,
                                        fontWeight: 900,
                                        color: '#111',
                                        marginBottom: 8,
                                    }}
                                >
                                    {scanResult.message}
                                </div>
                                <div style={{ color: lineGrey, fontSize: 14, marginBottom: 18 }}>
                                    {t('qr-code.rescanHint')}
                                </div>
                            </>
                        )}

                        <button
                            type="button"
                            onClick={onClickRestart}
                            style={{
                                width: '100%',
                                height: 52,
                                borderRadius: 12,
                                border: `1px solid ${borderLight}`,
                                background: '#111',
                                color: '#fff',
                                fontSize: 16,
                                fontWeight: 900,
                                cursor: 'pointer',
                                marginBottom: 10,
                            }}
                        >
                            {t('qr-code.rescan')}
                        </button>
                        <button
                            type="button"
                            onClick={onClickGoHome}
                            style={{
                                width: '100%',
                                height: 52,
                                borderRadius: 12,
                                border: `1px solid ${borderLight}`,
                                background: '#fff',
                                color: '#111',
                                fontSize: 16,
                                fontWeight: 800,
                                cursor: 'pointer',
                            }}
                        >
                            {t('qr-code.backHome')}
                        </button>
                    </div>
                </div>
            ) : null}
        </section>
    );
};

QrcodePage.propTypes = {
    heroData: PropTypes.shape({
        image: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
    }),
};

export default QrcodePage;
